import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Container } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import {
  PersistReduxAction,
  Verbiage,
  maxExportSizeLimit,
} from '../../constants';
import { debounce } from '../../helpers/debounce';
import smartHttp from '../../helpers/smartHttp';
import AgGridComponent from '../../sharedcomponents/ag-grid/AgGrid';
import {
  getFilterColumns,
  resizeColumnAsPerValue,
  getGroupByColumns,
  getSortColumns,
} from '../../sharedcomponents/ag-grid/ag-grid-helper';
import CheckedIcon from './../../assets/icons/common/checked.png';
import { ReactComponent as Resetcon } from './../../assets/icons/common/reset.svg';
import { ReactComponent as CloneIcon } from './../../assets/icons/common/clone.svg';
import { ReactComponent as CloudIcon } from './../../assets/icons/common/cloud.svg';
import { ReactComponent as DeleteIcon } from './../../assets/icons/common/delete.svg';
import { ReactComponent as ExportIcon } from './../../assets/icons/common/export.svg';
import { ReactComponent as AddUserIcon } from './../../assets/icons/users/add-user.svg';
import Loader from './../../components/Loader';
import {
  Endpoints,
  UrlPath,
  loaderStyle,
  ContractDisplayOptions,
  superUserGroup,
  csaUserGroup,
  viewerUserGroup,
} from './../../constants';
import { persistDataStore } from './../../redux/persistStore';
import store from './../../redux/store';
import { Notify } from './../../sharedcomponents/Alert/Notify';
import AddEditUser from './add-edit-user/add-edit-user';
import BulkUploadUsers from './bulk-upload-users/bulk-upload-users';
import CloneUserEntitlements from './clone-user-entitlements/clone-user-entitlements';
import FileSaver from 'file-saver';

const UserDetails = ({
  contracts,
  accounts,
  contractDisplayOption,
  isViewerUser,
  activeTab,
  callBackTotalUsers,
  isBPMReferral,
  locationFilter,
  clearLocationFilter,
}) => {
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(false);
  const [showBulkUsers, setShowBulkUsers] = useState(false);
  const [showCloneUserEntitlements, setShowCloneUserEntitlements] =
    useState(false);
  const [editData, setEditData] = useState();
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [cloneToUser, setCloneToUser] = useState();
  const [cloneEntitlementData, setCloneEntitlementData] = useState();
  const [inputValue, setInputValue] = useState('');
  const [isExportInprogress, setIsExportInprogress] = useState(false);
  const [hasVisibleColumns, setHasVisibleColumns] = useState(true);

  // user belongs to CSA or viewer group
  const groups = store.getState()?.userInfo?.userInfo?.SPGGroups || [];
  const isSuperUser = groups.includes(superUserGroup);
  const isCSAUser =
    isSuperUser ||
    groups.includes(csaUserGroup) ||
    groups.includes(viewerUserGroup);
  const isViewerRestricted = isViewerUser && !isSuperUser;

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const handleCloseBulkUers = () => setShowBulkUsers(false);
  const handleShowBulkUsers = () => setShowBulkUsers(true);

  const handleCloseCloneUserEntitlements = () => {
    setShowCloneUserEntitlements(false);
    setCloneEntitlementData();
    setCloneToUser();
  };
  const handleCloneUserEntitlementsSuccess = () => {
    setCloneToUser();
    smartHttp.clearCache(Endpoints.userentitlementsApi);
    smartHttp.clearCache(Endpoints.distributorUserApi);
    gridRef.current.api.onFilterChanged();
  };
  const searchInputRef = useRef(null);
  const gridRef = useRef();
  const rowDataRef = useRef([]);
  // Tracks the last contract filter string we sent to the grid.
  // Using a ref (not getFilterModel) so the comparison is immune to
  // other code mutating the filter model between two handler calls.
  const prevContractFilterRef = useRef(null);
  const navigate = useNavigate();

  const isFirstColumn = (params) => {
    let displayedColumns = params.api.getAllDisplayedColumns();
    let thisIsFirstColumn = displayedColumns[0] === params.column;
    if (thisIsFirstColumn) {
      return thisIsFirstColumn;
    }
  };

  const isSameOrFirstColumn = (params) => {
    // Show checkbox on each row of first column and on grouped, show it only on grouped columns
    let displayedColumns = params?.api?.getAllDisplayedColumns();
    let showCheckBox = displayedColumns[params?.node?.level] === params?.column;
    return showCheckBox;
  };

  const defaultConfig = {
    editable: true,
    isExportCSV: false,
    suppressDragLeaveHidesColumns: true,
    isExportExcel: false,
    pivotPanelShow: '',
    pagination: false,
    isAutoSizeColums: true,
    enableCharts: false,
    rowGroupPanelShow: false,
    sideBar: true,
    suppressColumnVirtualisation: true,
    overlayNoRowsTemplate:
      contracts && contracts.some((x) => x.checked)
        ? Verbiage.users
        : Verbiage.noData,
  };

  useEffect(() => {
    const reduceCallback = setTimeout(() => {
      contractChangeHandler();
    }, 1000);
    return () => clearTimeout(reduceCallback);
  }, [contracts]);

  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [activeTab]);

  const columnFilterParams = {
    filter: 'agTextColumnFilter',
    enableRowGroup: false,
    enablePivot: false,
    menuTabs: ['generalMenuTab', 'filterMenuTab', 'columnsMenuTab'],
  };

  const defaultColdef = {
    ...columnFilterParams,
    sortable: true,
    resizable: true,
    checkboxSelection: !isViewerRestricted && isSameOrFirstColumn,
    headerCheckboxSelection: !isViewerRestricted && isFirstColumn,
  };

  const getContractDisplayColumns = (contractDisplayType) => {
    return !(
      contractDisplayType === contractDisplayOption?.preferenceValue ||
      contractDisplayOption?.preferenceValue ===
        ContractDisplayOptions[2].contractDisplayOption
    );
  };

  const columns = [
    {
      headerName: '',
      field: 'contractNumber',
      resizable: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: [
          'inRange',
          {
            displayKey: 'inRange',
            displayName: 'Range Between',
            predicate: ([filterValue], cellValue) => {
              return cellValue == null || cellValue < filterValue;
            },
          },
        ],
      },
      suppressHeaderMenuButton: true,
      sortable: false,
      hide: true,
      suppressColumnsToolPanel: true,
    },
    {
      headerName: 'Email ID',
      headerTooltip: 'Email ID',
      field: 'email',
      initialFlex: 3,
      lockPosition: true,
      cellRenderer: (params) => {
        if (params.value != null) {
          return (
            <div
              className="email-label"
              title={params.value}
              onClick={() => {
                setEditData(params.data);
                setShow(true);
              }}
            >
              {params.value}
            </div>
          );
        } else return '';
      },
    },
    {
      headerName: 'Name',
      headerTooltip: 'Name',
      field: 'contactName',
      tooltipField: 'contactName',
      initialFlex: 3,
    },
    {
      headerName: 'Account',
      headerTooltip: 'Account',
      field: 'account',
      tooltipField: 'account',
      initialFlex: 3,
    },
    {
      headerName: 'Location',
      headerTooltip: 'Location',
      field: 'officeLocation',
      tooltipField: 'officeLocation',
      initialFlex: 3,
    },
    {
      headerName: 'Job Function',
      headerTooltip: 'Job Function',
      field: 'jobFunction',
      tooltipField: 'jobFunction',
      initialFlex: 3,
    },
    {
      headerName: 'Role',
      headerTooltip: 'Role',
      field: 'designation',
      tooltipField: 'designation',
      initialFlex: 3,
      initialHide: true,
    },
    {
      headerName: 'Department',
      headerTooltip: 'Department',
      field: 'department',
      tooltipField: 'department',
      initialFlex: 3,
      initialHide: true,
    },
    // Commented out Email Marketing as don't require this for now
    // {
    //   headerName: 'Email Marketing',
    //   headerTooltip: 'Email Marketing',
    //   field: 'isEmailOptOut',
    //   tooltipField: 'isEmailOptOutTooltip',
    //   cellRenderer: ({ data }) => {
    //     if (data?.isEmailOptOut !== 1) {
    //       return <img src={CheckedIcon} className="width-20" />;
    //     } else {
    //       return '';
    //     }
    //   },
    //   initialFlex: 3,
    //   initialHide: true,
    //   menuTabs: ['generalMenuTab', 'columnsMenuTab'],
    // },
    {
      headerName: 'Email Processed',
      headerTooltip: 'Email Processed',
      field: 'emailProcessed',
      tooltipField: 'emailProcessed',
      initialFlex: 3,
      initialHide: true,
    },
    {
      headerName: 'Subscription Type',
      headerTooltip: 'Subscription Type',
      field: 'contractType',
      tooltipField: 'contractType',
      initialFlex: 3,
    },
    {
      headerName: 'Contract ID',
      headerTooltip: 'Contract ID',
      field: 'allContractNumber',
      tooltipField: 'allContractNumber',
      initialFlex: 3,
      initialHide: true,
      suppressColumnsToolPanel: false,
    },
    // {
    //   headerName: 'Contract Description',
    //   headerTooltip: 'Contract Description',
    //   field: 'contractDescription',
    //   tooltipField: 'contractDescription',
    //   initialFlex: 3,
    //   ...columnFilterParams,
    //   initialHide: getContractDisplayColumns(
    //     ContractDisplayOptions[1].contractDisplayOption,
    //   ),
    //   suppressColumnsToolPanel: getContractDisplayColumns(
    //     ContractDisplayOptions[1].contractDisplayOption,
    //   ),
    // },
    {
      headerName: 'Onboarded',
      field: 'contractStatus',
      tooltipField: 'contractStatus',
      initialFlex: 2,
      initialHide: !isCSAUser, // show only for CSA users
      cellRenderer: (params) => {
        if (params?.value === 'Active') {
          return <img src={CheckedIcon} className="width-20" />;
        } else return '';
      },
    },
    {
      headerName: 'Group Name',
      headerTooltip: 'Group Name',
      field: 'groupName',
      tooltipField: 'groupName',
      initialFlex: 3,
      cellRenderer: (params) => {
        if (params.value != null) {
          return (
            <div
              className="email-label"
              title={params.value}
              onClick={() => {
                // Redirect to user group page and open edit group window
                navigate(UrlPath.userGroup, {
                  state: { userGroupAction: params.data },
                });
              }}
            >
              {params.value}
            </div>
          );
        } else return '';
      },
    },
    {
      headerName: 'Entitled',
      headerTooltip: 'Entitled',
      field: 'entitled',
      tooltipField: 'entitled',
      initialFlex: 2,
      cellRenderer: ({ data }) => {
        if (data?.entitled === 'Yes') {
          return <img src={CheckedIcon} className="width-20" />;
        } else return '';
      },
    },
  ];

  const getRowId = (params) => {
    if (
      params?.data &&
      params.data.contactId &&
      params.data.contractType &&
      params.data.contractStatus &&
      params.data.allContractNumber
    ) {
      return (
        'user-' +
        params.data.contactId +
        '-' +
        params.data.contractType +
        '-' +
         params.data.contractStatus +
        '-' +
        params.data.allContractNumber
      );
    }
  };

  const gridStyle = { height: '83vh' };

  const [buttonIcons, setButtonIcons] = useState({
    CloneIcon: false,
    DeleteIcon: false,
    ExportIcon: false,
    UserGroupIcon: false,
    ResetIcon: false,
  });

  // set input to CSA selected user
  useEffect(() => {
    let csaActionData =
      persistDataStore.getState()?.userActionData?.csaUserActionInfo;
    if (
      csaActionData?.type &&
      csaActionData?.value &&
      csaActionData?.type === 'user'
    ) {
      setInputValue(csaActionData?.value);
    }
    if (
      csaActionData?.type &&
      csaActionData?.type === 'account' &&
      csaActionData?.data?.account
    ) {
      setInputValue(csaActionData?.data?.account);
    }
  }, []);

  // Apply location filter when navigating from Locations tab User Count click
  useEffect(() => {
    if (locationFilter && gridRef.current) {
      const filterModel = gridRef.current.api.getFilterModel();
      filterModel['officeLocation'] = {
        filterType: 'text',
        type: 'contains',
        filter: locationFilter,
      };
      // setFilterModel already fires onFilterChanged internally in SSRM —
      // do NOT call onFilterChanged() again or getRows fires twice.
      gridRef.current.api.setFilterModel(filterModel);
      setButtonIcons((prev) => ({ ...prev, ResetIcon: true }));
      if (clearLocationFilter) clearLocationFilter();
    }
  }, [locationFilter, activeTab]);

  // Refresh user grid when a location transfer mapping is saved
  const locationTransferSavedAt = useSelector((state) => state.locationTab?.locationTransferSavedAt);
  useEffect(() => {
    if (locationTransferSavedAt && gridRef.current) {
      gridRef.current.api.onFilterChanged();
    }
  }, [locationTransferSavedAt]);

  const onFilterTextBoxChanged = useCallback(({ target }) => {
    let searchText = target.value.trim();
    if (!searchText || searchText.length > 2) {
      gridRef.current.api.onFilterChanged();
    }
  }, []);

  const onFilterTextBoxChangedDebounce = debounce(onFilterTextBoxChanged, 1000);

  const onGridReadyHandler = (params) => {
    gridRef.current = params;
    gridRef.current.api.setGridOption(
      'serverSideDatasource',
      getServerSideDatasource(),
    );
    contractChangeHandler();
  };

  const onFirstDataRenderedHandler = (params) => {
    resizeColumnAsPerValue(params, rowDataRef.current);
  };

  const onGridSizeChangedHandler = (params) => {
    // Defer to next animation frame so offsetWidth is read after the browser
    // has finished reflowing the layout (e.g. after zoom level changes).
    requestAnimationFrame(() => {
      resizeColumnAsPerValue(params, rowDataRef.current);
    });
  };

  const getServerSideDatasource = () => {
    return {
      getRows: (params) => {
        try {
          let AgParams = params?.request;
          let contractFilterModel =
            AgParams?.filterModel['contractNumber']?.filter;

          if (
            contractFilterModel?.length > 0 &&
            contractFilterModel !== 'NoContractSelected'
          ) {
            let FilterColumns =
              'email,contactName,contractType,account,officeLocation,jobFunction,entitled,groupName,emailProcessed,department,designation,allContractNumber' +
              (contractDisplayOption?.preferenceValue ===
                ContractDisplayOptions[2].contractDisplayOption ||
              contractDisplayOption?.preferenceValue ===
                ContractDisplayOptions[0].contractDisplayOption
                ? ',contractNumber'
                : '') +
              (contractDisplayOption?.preferenceValue ===
                ContractDisplayOptions[2].contractDisplayOption ||
              contractDisplayOption?.preferenceValue ===
                ContractDisplayOptions[1].contractDisplayOption
                ? ',contractDescription'
                : '');
            let searchText = document
              .getElementById('user-search-filter-input')
              .value.trim();
            let queryParams = {
              Filter: getFilterColumns(AgParams),
              FilteredColumns: JSON.stringify(
                gridRef.current.api.getFilterModel(),
              ),
              Field:
                'country,contactName,contactId,groupid,groupName,contractType,email,phone,department,jobFunction,designation,isEmailOptOut,emailProcessed,account,accountId,officeLocation,officeLocationId,entitled,modifiedDate,contractStatus,allContractNumber',

              Sort: getSortColumns(
                AgParams,
                'modifiedDate:desc,contactId:desc,contractType:asc',
              ),
              PageSize: AgParams.endRow - AgParams.startRow,
              Page:
                AgParams.startRow / (AgParams.endRow - AgParams.startRow) + 1,
              groupBy: getGroupByColumns(AgParams),
              querySearch: searchText,
              searchColumns: searchText ? FilterColumns : '',
              id: String(Date.now()),
            };
            
            gridRef.current.api.showLoadingOverlay();
            smartHttp
              .get(Endpoints.userApi, queryParams, { useCache: false })
              .then(({ data }) => {
                let rowData = data?.results ?? data?.aggResultValue;
                callBackTotalUsers(data?.metadata.count);
                // new key created for tooltip
                rowData = rowData.map((el) => {
                  el.isEmailOptOutTooltip =
                    el.isEmailOptOut !== 1 ? 'Yes' : 'No';
                  return { ...el };
                });

                rowDataRef.current = rowData;

                params.success({
                  rowData: rowData,
                  rowCount: data?.metadata.count,
                });

                // show user details if CSA selected a user from CSA dashboard
                let ccaData =
                  persistDataStore.getState()?.userActionData
                    ?.csaUserActionInfo;
                if (
                  ccaData &&
                  ccaData.type === 'user' &&
                  ccaData.showUserDetails
                ) {
                  setEditData(rowData[0]);
                  setShow(true);
                  ccaData.showUserDetails = false;
                  persistDataStore.dispatch({
                    type: PersistReduxAction.csaActionData,
                    payload: ccaData,
                  });
                }

                if (rowData?.length === 0)
                  gridRef.current.api.showNoRowsOverlay();
                else {
                  gridRef.current.api.hideOverlay();
                }
              })
              .catch(() => {
                params.fail();
                gridRef.current.api.hideOverlay();
              });

          } else {
            params.success({
              rowData: [],
              rowCount: 0,
            });

            if (contractFilterModel === 'NoContractSelected') {
              gridRef.current.api.showNoRowsOverlay();
            }
          }
        } catch (err) {
          params.fail();
        }
      },
    };
  };

  const contractChangeHandler = () => {
    if (gridRef.current && contracts && contracts.length > 0) {
      let filteredContracts = contracts.filter((x) => x.checked);
      let filterValue = {
        filter:
          filteredContracts.length !== 0
            ? filteredContracts
                .map((t) =>
                  t.contractNumber ? '"' + t.contractNumber + '"' : '',
                )
                .join(',')
            : 'NoContractSelected',
        filterType: 'text',
        type: 'inRange',
      };

      gridRef.current.api.setFilterModel(
        getAllFilterModel('contractNumber', filterValue),
      );
    } else {
      gridRef.current.api.showNoRowsOverlay();
    }
  };

  const getAllFilterModel = (colId, newModel) => {
    // Clone the current model to avoid mutating the grid's internal object
    const currentModel = { ...(gridRef.current.api.getFilterModel() || {}) };
    currentModel[colId] = newModel;
    return currentModel;
  };

  const getUsers = () => {
    gridRef.current.api.onFilterChanged();
    gridRef.current.api.ensureIndexVisible(0, 'top');
  };

  const onSelectionChanged = (elements) => {
    let rowLength = elements.api.getSelectedRows().length;
    setSelectedUsers(elements.api.getSelectedRows());
    setButtonIcons({
      ...buttonIcons,
      CloneIcon: rowLength > 0,
      DeleteIcon: rowLength > 0,
      ExportIcon: rowLength > 0,
      UserGroupIcon: rowLength > 1,
    });
  };

  const onSelectionChangedDebounce = debounce(onSelectionChanged, 100);

  const onColumnVisibleHandler = (params) => {
    const displayedColumns =
      gridRef.current?.api?.getAllDisplayedColumns() || [];
    // Exclude checkbox-only columns (no field) from the count
    const dataColumns = displayedColumns.filter(
      (col) => col.getColDef()?.field,
    );
    setHasVisibleColumns(dataColumns.length > 0);
    requestAnimationFrame(() => {
      resizeColumnAsPerValue(params, rowDataRef.current);
    });
  };

  const getDate = () => {
    let currentDate = new Date();
    return (
      currentDate.getDate() +
      '-' +
      (currentDate.getMonth() + 1) +
      '-' +
      currentDate.getFullYear()
    );
  };
  const isSelectAllCheckboxChecked = () => {
    let serverSideSelectionState =
      gridRef.current?.api?.getServerSideSelectionState();
    let selectAllCheckbox = false;
    if (serverSideSelectionState?.selectAllChildren) {
      if (
        serverSideSelectionState?.toggledNodes &&
        serverSideSelectionState?.toggledNodes.length > 0
      ) {
        selectAllCheckbox = false;
      } else {
        selectAllCheckbox = true;
      }
    }
    return selectAllCheckbox;
  };

  // Essential fields always included in export (needed for identification, sorting, internal operations)
  const essentialExportFields = [
    'contactId',
    'groupid',
    'accountId',
    'officeLocationId',
    'modifiedDate',
  ];

  const getVisibleFieldsFromGrid = () => {
    const fallbackFields =
      'contactName,contactId,groupid,groupName,contractType,email,phone,department,jobFunction,designation,isEmailOptOut,emailProcessed,account,accountId,officeLocation,officeLocationId,entitled,modifiedDate,contractStatus,allContractNumber';
    try {
      const api = gridRef.current?.api;
      if (!api) return fallbackFields;

      const displayedColumns = api.getAllDisplayedColumns() || [];
      const fieldSet = new Set();

      // Add fields from currently visible columns
      displayedColumns.forEach((col) => {
        const field = col.getColDef()?.field;
        if (field) fieldSet.add(field);
      });

      // Always include essential fields
      essentialExportFields.forEach((f) => fieldSet.add(f));

      return fieldSet.size > 0
        ? Array.from(fieldSet).join(',')
        : fallbackFields;
    } catch {
      return fallbackFields;
    }
  };

  const getUsersQueryParams = async () => {
    try {
      if (gridRef) {
        const AgParams = gridRef.current?.api;
        let FilterColumns =
          'email,contactName,contractType,account,officeLocation,jobFunction,entitled,groupName,emailProcessed,department,designation,allContractNumber';
        let searchText = document
          .getElementById('user-search-filter-input')
          .value.trim();

        const currentFilterModel = { filterModel: AgParams?.getFilterModel() };
        // If contractType column is visible, include it in the sort order; otherwise, exclude it.
        let contractTypeSortDefault = 'modifiedDate:desc,contactId:desc';
        
        if ((gridRef.current?.api?.getAllDisplayedColumns() || []).some(
              (c) => c.getColDef?.().field === 'contractType',
            )) {
          contractTypeSortDefault += ',contractType:asc';
        }
        let queryParams = {
          Page: 1,
          PageSize: maxExportSizeLimit,
          Filter: await getFilterColumns(currentFilterModel),
          FilteredColumns: await JSON.stringify(currentFilterModel.filterModel),
          Field: getVisibleFieldsFromGrid(),
          Sort: await getSortColumns(
            AgParams,
            contractTypeSortDefault
          ),
          querySearch: searchText,
          searchColumns: searchText ? FilterColumns : '',
          id: String(Date.now()),
        };

        return queryParams;
      }
    } catch (error) {}
  };

  const exportUserCSV = async () => {
    if (!hasVisibleColumns) return;

    // Check if rows are selected
    const selectedRows = gridRef.current?.api?.getSelectedRows();
    const hasSelectedRows = selectedRows && selectedRows.length > 0;

    if (!hasSelectedRows) {
      Notify({
        alert: true,
        type: 'warning',
        title: 'Please select at least one user to export',
      });
      return;
    }

    if (isExportInprogress) {
      return;
    }

    try {
      setIsExportInprogress(true);
      Notify({
        alert: true,
        type: '',
        title: 'Your download is in progress',
      });

      let successTitle =
        'Please note that the exported file contains maximum of ' +
        maxExportSizeLimit +
        ' records. Please download additional records (if any) separately';

      // Check if all rows are selected (select all checkbox)
      const isAllSelected = isSelectAllCheckboxChecked();

      // Save current column state before export to restore after
      const savedColumnState = gridRef.current?.api?.getColumnState();

      if (isAllSelected) {
        // Export all filtered data from server
        const queryParams = await getUsersQueryParams();
        queryParams.AutoFit = true;

        await smartHttp
          .post(
            Endpoints.userExportApi,
            queryParams,
            {},
            {
              axiosConfig: { responseType: 'blob' },
            },
          )
          .then((response) => {
            const totalCount = response.headers['total-count'];
            if (totalCount < maxExportSizeLimit) {
              FileSaver.saveAs(response.data, 'Users_' + getDate() + '.xlsx');
              setIsExportInprogress(false);
              Notify({
                alert: true,
                type: 'success',
                title: successTitle,
                dialogClassName: 'alert-export-success-max-width',
              });
            } else {
              setIsExportInprogress(false);
              Notify({
                alert: true,
                type: 'error',
                dialogClassName: 'alert-export-success-max-width',
                title:
                  'Number of records for export ' +
                  totalCount +
                  ' are greater than the allowed limit (' +
                  maxExportSizeLimit +
                  '). Please adjust the export criteria to ensure that the number of records for export are within allowed limit.',
              });
            }
          })
          .catch((error) => {
            setIsExportInprogress(false);
            Notify({
              alert: true,
              type: 'error',
              title: 'Something went wrong',
            });
          })
          .finally(() => {
            // Restore column state after server-side export
            if (savedColumnState) {
              gridRef.current?.api?.applyColumnState({
                state: savedColumnState,
                applyOrder: true,
              });
            }
          });
      } else {
        // Export only selected rows (client-side) with auto-fit columns using Excel format
        setTimeout(() => {
          // Cache selected rows data BEFORE creating callback
          const selectedRowsData =
            gridRef.current?.api?.getSelectedRows() || [];

          // Validate selected rows count against max export limit
          if (selectedRowsData.length > maxExportSizeLimit) {
            setIsExportInprogress(false);
            Notify({
              alert: true,
              type: 'error',
              dialogClassName: 'alert-export-success-max-width',
              title:
                'Number of records for export ' +
                selectedRowsData.length +
                ' are greater than the allowed limit (' +
                maxExportSizeLimit +
                '). Please select fewer records to export.',
            });
            return;
          }

          // Define columnWidth callback for dynamic width calculation
          const columnWidthCallback = (params) => {
            const column = params.column;
            const field = column?.getColDef().field;
            const colDef = column.getColDef();

            if (!field) return 25;

            let maxLength = field.length;

            // Calculate max length from CACHED selected rows
            for (const row of selectedRowsData) {
              let value;

              if (colDef.valueGetter) {
                value = colDef.valueGetter({
                  data: row,
                  node: { data: row },
                  colDef: colDef,
                  api: gridRef.current?.api,
                  columnApi: gridRef.current?.columnApi,
                });
              } else {
                value = row[field];
              }

              const str = value != null ? String(value) : '';
              if (str.length > maxLength) {
                maxLength = str.length;
              }
            }

            // Return calculated width: 6 is per character width & 40 is marginal width (extra padding)
            return Math.min(Math.max(maxLength * 6 + 60, 25), 300);
          };

          // Export as Excel with auto-fit column widths
          gridRef.current.api.exportDataAsExcel({
            onlySelected: true,
            fileName: 'Users_' + getDate(),
            columnWidth: columnWidthCallback,
          });

          // Restore column state after client-side export
          if (savedColumnState) {
            gridRef.current?.api?.applyColumnState({
              state: savedColumnState,
              applyOrder: true,
            });
          }

          setIsExportInprogress(false);
          Notify({
            alert: true,
            type: 'success',
            title: successTitle,
            dialogClassName: 'alert-export-success-max-width',
          });
        }, 100);
      }
    } catch (error) {
      setIsExportInprogress(false);
      Notify({
        alert: true,
        type: 'error',
        title: 'Something went wrong',
      });
    }
  };

  const openDeletedSuccess = (title) => {
    Notify({
      alert: true,
      type: 'success',
      title: title,
    });
  };

  const deleteUsers = () => {
    let deleteUserIds = [];
    let selectedUsers = gridRef.current.api.getSelectedRows();
    if (selectedUsers && selectedUsers.length > 0) {
      selectedUsers.map((user) => deleteUserIds.push(user.contactId));
      let deleteSuccessTitle =
        deleteUserIds.length > 1 ? 'Users Deleted' : 'User Deleted';

      setLoading(true);
      smartHttp
        .post(Endpoints.userApi + Endpoints.deleteEndpoint, deleteUserIds)
        .then(({ data }) => {
          if (data) {
            openDeletedSuccess(deleteSuccessTitle);
            gridRef.current.api
              .getSelectedNodes()
              .forEach((node) => node.setSelected(false));
            gridRef.current.api.onFilterChanged();
          }
        })
        .catch((err) => {})
        .finally(() => setLoading(false));
    }
  };

  const openStatusModal = (title) => {
    Notify({
      alert: true,
      type: 'warning',
      title: title,
      confirmBtnText: 'YES DELETE',
      cancelBtnText: 'NO, KEEP IT',
      onConfirm: () => deleteUsers(),
    });
  };

  const getDeletedTitle = () => {
    let selectedUsers = gridRef.current.api.getSelectedRows();
    if (selectedUsers && selectedUsers.length > 0) {
      switch (true) {
        case selectedUsers.length === 1 &&
          selectedUsers.every((us) => us.entitled === 'Yes'):
          return 'This user has active entitlements , are you sure you want to delete the user?';
        case selectedUsers.length === 1 &&
          selectedUsers.every((us) => us.entitled === 'No'):
          return 'Are you sure you want to permanently delete the user?';
        case selectedUsers.length > 1 &&
          selectedUsers.every((us) => us.entitled === 'Yes'):
          return 'All users have active entitlements , are you sure you want to delete the users?';
        case selectedUsers.length > 1 &&
          selectedUsers.every((us) => us.entitled === 'No'):
          return 'Are you sure you want to permanently delete the users?';
        default:
          return 'Some users have active entitlements , are you sure you want to delete the user?';
      }
    }
    return '';
  };

  const deleteClickHandler = () => {
    if (buttonIcons.DeleteIcon) {
      let title = getDeletedTitle();
      openStatusModal(title);
    }
  };

  const cloneClickHandler = () => {
    if (!buttonIcons.CloneIcon) return;
    let selectedUsers = gridRef.current.api.getSelectedRows();

    switch (true) {
      case selectedUsers.length > 1:
        Notify({
          alert: true,
          type: 'warning',
          title: 'You can only select a maximum of 1 row for cloning.',
          confirmBtnText: 'OK',
        });
        break;
      case !selectedUsers.every(
        (x) => x.contractNumber === selectedUsers[0].contractNumber,
      ):
        Notify({
          alert: true,
          type: 'warning',
          title:
            'Please select users from the same contract number to begin cloning',
          confirmBtnText: 'OK',
        });
        break;
      case selectedUsers.some((x) => x.groupId):
        Notify({
          alert: true,
          type: 'warning',
          title: 'User belongs to group will not be allowed for clone',
          confirmBtnText: 'OK',
        });
        break;
      default:
        setShowCloneUserEntitlements(true);
    }
  };

  const handleCloneFromEditUser = (newData) => {
    setShowCloneUserEntitlements(true);
    setCloneToUser([newData]);
  };

  return (
    <>
      {show && (
        <AddEditUser
          show={show}
          contracts={contracts}
          handleClose={handleClose}
          editData={editData}
          setEditData={setEditData}
          getUsers={getUsers}
          accounts={accounts}
          contractDisplayOption={contractDisplayOption}
          handleCloneFromEditUser={(p) => handleCloneFromEditUser(p)}
          setCloneEntitlementData={(o) => setCloneEntitlementData(o)}
          isViewerUser={isViewerUser}
          isBPMReferral={isBPMReferral}
        />
      )}
      {showBulkUsers && (
        <BulkUploadUsers
          showBulkUsers={showBulkUsers}
          handleCloseBulkUers={handleCloseBulkUers}
          contracts={contracts}
          accounts={accounts}
          getUpdatedUsers={getUsers}
          isBPMReferral={isBPMReferral}
        />
      )}
      {showCloneUserEntitlements && (
        <CloneUserEntitlements
          showCloneUserEntitlements={showCloneUserEntitlements}
          handleCloseCloneUserEntitlements={handleCloseCloneUserEntitlements}
          handleCloneUserEntitlementsSuccess={
            handleCloneUserEntitlementsSuccess
          }
          selectedToUsers={cloneToUser ? cloneToUser : selectedUsers}
          cloneEntitlementData={cloneEntitlementData}
          allContracts={contracts}
          isBPMReferral={isBPMReferral}
        />
      )}

      {loading && <Loader type="scaleLoader" cssClass={loaderStyle} />}

      <Container fluid>
        <div className="row mt-2">
          <div className="col-6 d-flex">
            <input
              type="text"
              id="user-search-filter-input"
              className="form-control search-box"
              defaultValue={inputValue}
              placeholder="Search users"
              onInput={onFilterTextBoxChangedDebounce}
              ref={searchInputRef}
            />
          </div>
          <div className="col-6 my-1 action-item-bar d-flex justify-content-end align-items-center">
            {isViewerRestricted ? (
              !isBPMReferral && (
                <span className="user-grid-icons">
                  <ExportIcon
                    className={
                      buttonIcons.ExportIcon && hasVisibleColumns
                        ? 'icon-active'
                        : 'cursor-default'
                    }
                    alt="Export Active Users Report"
                    width="18"
                    title={
                      hasVisibleColumns
                        ? 'Export Active Users Report'
                        : 'Please select at least one column to export'
                    }
                    onClick={exportUserCSV}
                    style={{
                      cursor: !hasVisibleColumns
                        ? 'not-allowed'
                        : buttonIcons.ExportIcon
                          ? 'pointer'
                          : 'default',
                    }}
                  />
                </span>
              )
            ) : (
              <>
                <span className="user-grid-icons icon-divider">
                  <Resetcon
                    alt="Reset"
                    width="18"
                    title="Reset"
                    className={`reset-icon ${buttonIcons.ResetIcon ? 'icon-active' : 'cursor-default'}`}
                    onClick={() => {
                      if (buttonIcons.ResetIcon && gridRef.current) {
                        const filterModel =
                          gridRef.current.api.getFilterModel();
                        delete filterModel['officeLocation'];
                        gridRef.current.api.setFilterModel(filterModel);
                        gridRef.current.api.onFilterChanged();
                        setButtonIcons((prev) => ({
                          ...prev,
                          ResetIcon: false,
                        }));
                      }
                    }}
                  />
                </span>
                <span className="user-grid-icons icon-divider">
                  <CloneIcon
                    className={
                      buttonIcons.CloneIcon ? 'icon-active' : 'cursor-default'
                    }
                    alt="Clone entitlements"
                    width="18"
                    title="Clone entitlements"
                    onClick={cloneClickHandler}
                  />
                </span>
                <span className="user-grid-icons icon-divider">
                  <DeleteIcon
                    className={
                      buttonIcons.DeleteIcon ? 'icon-active' : 'cursor-default'
                    }
                    alt="Delete Users"
                    width="18"
                    title="Please select at least one user to delete from the user list"
                    onClick={deleteClickHandler}
                  />
                </span>
                {!isBPMReferral && (
                  <span className="user-grid-icons icon-divider">
                    <ExportIcon
                      className={
                        buttonIcons.ExportIcon && hasVisibleColumns
                          ? 'icon-active'
                          : 'cursor-default'
                      }
                      alt="Export Active Users Report"
                      width="18"
                      title={
                        hasVisibleColumns
                          ? 'Export Active Users Report'
                          : 'Please select at least one column to export'
                      }
                      onClick={exportUserCSV}
                      style={{
                        cursor: !hasVisibleColumns
                          ? 'not-allowed'
                          : buttonIcons.ExportIcon
                            ? 'pointer'
                            : 'default',
                      }}
                    />
                  </span>
                )}
                <span className="user-grid-icons icon-divider">
                  <AddUserIcon
                    className="icon-active"
                    alt="Add User"
                    width="18"
                    title="Add User"
                    onClick={handleShow}
                  />
                </span>
                <span className="user-grid-icons icon-divider">
                  <CloudIcon
                    alt="Import Users"
                    width="20"
                    title="Import Users"
                    onClick={handleShowBulkUsers}
                  />
                </span>
              </>
            )}
          </div>
        </div>
        <div className="ag-grid-table with-tabs mbx-12">
          <AgGridComponent
            config={defaultConfig}
            gridStyle={gridStyle}
            defaultColumnDef={defaultColdef}
            getRowId={getRowId}
            columns={columns}
            onGridReady={onGridReadyHandler}
            onFirstDataRendered={onFirstDataRenderedHandler}
            onGridSizeChanged={onGridSizeChangedHandler}
            onSelectionChanged={onSelectionChangedDebounce}
            onColumnVisible={onColumnVisibleHandler}
          />
        </div>
      </Container>
    </>
  );
};

export default UserDetails;
